import { useContext, useCallback, useMemo, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import { Typography, Box, Drawer, IconButton, Divider } from "@mui/material"
import { toLower } from "lodash"
import { Close, DragIndicator, AutoAwesome } from "@mui/icons-material"
import { useAIQuickAnswersMutation, ABORTED } from "hooks/ai-quick-answers"
import useResizeHandler from "hooks/resize-handler"
import useQuickAnswersContext from "connected-components/dashboard/context/quick-answers"
import { useAnalytics } from "connected-components/analytics"
import { QuickAnswersAnalyticsProvider, QUICK_ANSWERS_EVENTS } from "./analytics"
import { OK } from "helpers/data-statuses"
import { ContextInfo, QueryInput, QueryActions, QuickAnswersResults, ErrorDisplay } from "./components"
import { QUICK_ANSWERS_ERRORS } from "hooks/ai-quick-answers"
import { AI_DISCLAIMER } from "helpers/ai"
import { useQuickAnswersState } from "./hooks"

import "./styles.scss"

export default function QuickAnswersWrapper(props) {
  const { context: parentContext } = props
  const [QuickAnswersContext, contextValue] = useQuickAnswersContext(parentContext)

  return <QuickAnswersContext.Provider value={contextValue}>
    <QuickAnswersAnalyticsProvider>
      <QuickAnswers
        {...{
          ...props,
          context: QuickAnswersContext,
        }}
      />
    </QuickAnswersAnalyticsProvider>
  </QuickAnswersContext.Provider>
}

QuickAnswersWrapper.propTypes = {
  context: PropTypes.object.isRequired,
}

function QuickAnswers({ context, ...props }) {
  const { isAiDrawerOpen, setIsAiDrawerOpen, question: contextQuestion, setQuestion: setContextQuestion, instrument, endDate, startDate, earliestDate, latestDate } = useContext(context)
  const { instrumentsWithMetadata, modelMetadata } = props
  const { original, base } = instrumentsWithMetadata || {}

  const {
    contentWidth,
    isDragging,
    localQuestion,
    askedQuestion,
    results,
    lastUpdated,
    hasAskedQuestion,
    errorState,
    setErrorState,
    isLoadingSavedQuery,
    setLocalQuestion,
    setContentWidth,
    setIsDragging,
    askQuestionInitialize,
    askQuestionSuccess,
    askQuestionError,
    clearAll,
  } = useQuickAnswersState(contextQuestion)

  const analytics = useAnalytics()
  const questionStartTimeRef = useRef(null)

  useEffect(() => {
    if (toLower(instrument?.type) !== "portfolio") {
      setErrorState(QUICK_ANSWERS_ERRORS.PORTFOLIO_ONLY)
    }
  }, [instrument?.type, setErrorState])

  const { askQuestion, abortCurrentRequest, isLoading } = useAIQuickAnswersMutation(endDate, startDate)

  const { handleMouseDown } = useResizeHandler({
    isDragging,
    setIsDragging,
    setContentWidth,
    onDragEnd: async (finalWidth) => {
      setContentWidth(finalWidth)
      analytics.track(QUICK_ANSWERS_EVENTS.DRAWER_RESIZED, { width: finalWidth })
    },
    minWidthPercent: 25,
    maxWidthPercent: 50,
  })

  const handleClear = () => {
    clearAll()
    setContextQuestion("")
    analytics.track(QUICK_ANSWERS_EVENTS.QUESTION_CLEARED, {
      hadResults: !!results?.records?.length,
      hadQuestion: !!askedQuestion,
    })
  }

  const calculateTimeTakenInSeconds = () => {
    if (!questionStartTimeRef.current) {
      return null
    }
    const startTime = questionStartTimeRef.current
    questionStartTimeRef.current = null
    return (Date.now() - startTime) / 1000
  }

  const handleAskSuccess = useCallback((questionToAsk, result) => {
    const timeTaken = calculateTimeTakenInSeconds()

    askQuestionSuccess(result, QUICK_ANSWERS_ERRORS.NO_RESULTS)

    analytics.track(QUICK_ANSWERS_EVENTS.QUESTION_ANSWERED, {
      question: questionToAsk,
      timeTaken: `${timeTaken}s`,
    })
  }, [askQuestionSuccess, analytics])

  const handleAskError = useCallback((errorType, message) => {
    askQuestionError(errorType, message)
  }, [askQuestionError])

  const handleSuccessfulResponse = useCallback((result, errorType, message, questionToAsk) => {
    if (errorType === QUICK_ANSWERS_ERRORS.NO_RESULTS) {
      // Good question but no results
      askQuestionSuccess(result, errorType)
    } else if (errorType === QUICK_ANSWERS_ERRORS.BAD_QUESTION) {
      // Bad question - cannot answer
      handleAskError(errorType, message)
    } else if (!errorType && result) {
      // Successful response with records
      handleAskSuccess(questionToAsk, result)
    } else {
      // Unexpected response format
      handleAskError(QUICK_ANSWERS_ERRORS.AI_ERROR, message)
    }
  }, [askQuestionSuccess, handleAskError, handleAskSuccess])

  const handleAbort = useCallback(() => {
    // Abort the current request
    abortCurrentRequest()

    // Reset state to allow asking a question again
    setErrorState(null)

    // Track the abort event
    if (askedQuestion) {
      analytics.track(QUICK_ANSWERS_EVENTS.QUESTION_ABORTED, {
        question: askedQuestion,
        timeElapsed: calculateTimeTakenInSeconds(),
      })
    }
  }, [abortCurrentRequest, setErrorState, askedQuestion, analytics])

  const handleAsk = useCallback(async () => {
    const questionToAsk = localQuestion?.trim()
    if (!questionToAsk) {
      return
    }
    questionStartTimeRef.current = Date.now()

    askQuestionInitialize(questionToAsk)
    setContextQuestion(questionToAsk)

    analytics.track(QUICK_ANSWERS_EVENTS.QUESTION_ASKED, { question: questionToAsk })

    try {
      const [result, status, errorType, message] = await askQuestion({
        question: questionToAsk,
        modelId: modelMetadata?.id,
        resourceId: original?.alias || original?.id,
        resourceType: original?.type,
        base: base ? [base] : null,
      })

      if (status === OK) {
        handleSuccessfulResponse(result, errorType, message, questionToAsk)
      } else if (errorType !== ABORTED) {
        handleAskError(errorType, message)
      }
    } catch {
      handleAskError(QUICK_ANSWERS_ERRORS.SERVER_ERROR, null)
    }
  }, [localQuestion, askQuestionInitialize, original, base, modelMetadata, setContextQuestion, askQuestion, handleSuccessfulResponse, handleAskError, analytics])

  const handleClosePanel = () => {
    setIsAiDrawerOpen(false)

    analytics.track(QUICK_ANSWERS_EVENTS.DRAWER_CLOSED)
  }

  const handleRetry = useCallback(() => {
    if (askedQuestion) {
      setLocalQuestion(askedQuestion)
      handleAsk()
      analytics.track(QUICK_ANSWERS_EVENTS.ERROR_RETRY_CLICKED)
    }
  }, [askedQuestion, setLocalQuestion, handleAsk, analytics])

  const localQuestionRef = useRef(localQuestion)
  localQuestionRef.current = localQuestion

  const handleQuestionBlur = useCallback(() => {
    if (localQuestionRef.current !== contextQuestion) {
      setContextQuestion(localQuestionRef.current)
    }
  }, [contextQuestion, setContextQuestion])

  const canAsk = useMemo(() => {
    return localQuestion?.trim()
      && modelMetadata?.id
      && instrumentsWithMetadata?.original?.id
      && !isLoading
  }, [localQuestion, modelMetadata?.id, instrumentsWithMetadata?.original?.id, isLoading])

  return <Drawer
    anchor="right"
    open={isAiDrawerOpen}
    onClose={handleClosePanel}
    className="dashboard-quick-answers"
  >
    <Box
      className="content"
      style={{ width: contentWidth }}
    >
      <Box
        className="resize-handle"
        onMouseDown={handleMouseDown}
      >
        <DragIndicator fontSize="small" className="resize-icon" />
      </Box>
      <Box className="header">
        <AutoAwesome color="primary" />
        <Typography variant="h6">
          <span>AI Quick Answers</span><sup className="beta-tag"> beta</sup>
        </Typography>
        <Box className="header-actions">
          <IconButton
            onClick={handleClosePanel}
            size="medium"
            color="inherit"
          >
            <Close />
          </IconButton>
        </Box>
      </Box>
      <div className="scrollable-content">
        <Divider />
        <Box className="body">
          <Box>
            <ContextInfo
              resource={original}
              benchmark={base}
              model={modelMetadata}
              endDate={endDate}
              startDate={startDate}
              earliestDate={earliestDate}
              latestDate={latestDate}
            />
            <QueryInput
              question={localQuestion}
              onQuestionChange={(e) => setLocalQuestion(e.target.value)}
              onQuestionBlur={handleQuestionBlur}
              disabled={isLoadingSavedQuery || isLoading}
            />
          </Box>
          <QueryActions
            question={localQuestion}
            onClear={handleClear}
            onAsk={handleAsk}
            onAbort={handleAbort}
            disabled={!canAsk}
            isLoading={isLoading}
          />
        </Box>
        <Divider />
        <Box className="results-container">
          <ErrorDisplay errorState={errorState} onRetry={handleRetry} />
          <QuickAnswersResults
            results={results}
            lastUpdated={lastUpdated}
            question={askedQuestion}
            hasAskedQuestion={hasAskedQuestion}
            isLoading={isLoading}
            modelMetadata={modelMetadata}
            original={original}
          />
        </Box>
      </div>
      <Box className="ai-disclaimer">
        <Typography variant="caption">
          {AI_DISCLAIMER}
        </Typography>
      </Box>
    </Box>
  </Drawer>
}

QuickAnswers.propTypes = {
  context: PropTypes.object.isRequired,
  modelMetadata: PropTypes.object.isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
}
